import logger from '../../utils/logger';
import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, ResponsiveContainer, AreaChart, Area, ComposedChart } from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '@/contexts/ThemeContext';

interface ChartSpec {
  type: 'bar' | 'pie' | 'line' | 'dashboard' | 'scatter' | 'area' | 'combo' | 'composed';
  title: string;
  data: Array<{
    label?: string;
    value?: number;
    value2?: number;
    metric?: string;
    current?: number;
    target?: number;
    unit?: string;
    x?: number;
    y?: number;
    [key: string]: any;
  }>;
}

interface DeepReportRendererProps {
  content: string;
  className?: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// Fonction helper pour créer une clé stable basée sur le JSON
const createStableKey = (jsonString: string): string => {
  // Utiliser une partie du JSON comme clé pour identifier le graphique de manière unique
  const hash = jsonString.substring(0, 50).replace(/\s/g, '');
  return hash;
};

const DeepReportRenderer: React.FC<DeepReportRendererProps> = ({ content, className = '' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Styles tooltip selon le thème
  const tooltipStyle = isDark ? {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '0.5rem',
    color: '#fff'
  } : {
    backgroundColor: '#ffffff',
    border: '2px solid #e5e7eb',
    borderRadius: '0.5rem',
    color: '#000000',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  };
  
  // Fonction pour extraire le JSON complet avec comptage des accolades
  const extractCompleteJSON = (text: string, startIndex: number): { json: string; endIndex: number } | null => {
    let braceCount = 0;
    let inString = false;
    let escapeNext = false;
    let jsonStart = -1;

    for (let i = startIndex; i < text.length; i++) {
      const char = text[i];
      
      if (escapeNext) {
        escapeNext = false;
        continue;
      }
      
      if (char === '\\') {
        escapeNext = true;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        continue;
      }
      
      if (!inString) {
        if (char === '{') {
          if (braceCount === 0) jsonStart = i;
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0 && jsonStart !== -1) {
            return {
              json: text.substring(jsonStart, i + 1),
              endIndex: i + 1
            };
          }
        }
      }
    }
    
    return null;
  };

  // Fonction pour extraire et rendre les graphiques JSON
  const renderCharts = (text: string) => {
    const parts = [];
    let lastIndex = 0;
    let chartIndex = 0;
    const processedCharts = new Set<string>(); // Pour éviter les doublons

    // Collecter tous les matches de graphiques
    const allMatches = [];

    // Pattern 1: ```json ... ``` (ancien format)
    const jsonBlockPattern = /```json\s*/gi;
    let jsonMatch;
    while ((jsonMatch = jsonBlockPattern.exec(text)) !== null) {
      const jsonResult = extractCompleteJSON(text, jsonMatch.index + jsonMatch[0].length);
      if (jsonResult) {
        allMatches.push({
          start: jsonMatch.index,
          end: jsonResult.endIndex,
          jsonString: jsonResult.json,
          type: 'json',
          key: `json-${jsonMatch.index}`
        });
      }
    }

    // Pattern 2: **GRAPHIQUE OBLIGATOIRE X:** ... SPECIFICATION JSON: {...}
    const graphiquePattern = /\*\*GRAPHIQUE OBLIGATOIRE \d+:\*\*[\s\S]*?SPECIFICATION JSON:\s*/gi;
    let graphiqueMatch;
    while ((graphiqueMatch = graphiquePattern.exec(text)) !== null) {
      const jsonResult = extractCompleteJSON(text, graphiqueMatch.index + graphiqueMatch[0].length);
      if (jsonResult) {
        allMatches.push({
          start: graphiqueMatch.index,
          end: jsonResult.endIndex,
          jsonString: jsonResult.json,
          type: 'graphique',
          key: `graphique-${graphiqueMatch.index}`
        });
      }
    }

    // Trier les matches par position dans le texte
    allMatches.sort((a, b) => a.start - b.start);

    // Traiter chaque match dans l'ordre
    for (const match of allMatches) {
      // Éviter les doublons
      if (processedCharts.has(match.key)) {
        continue;
      }
      processedCharts.add(match.key);

      // Ajouter le texte avant le graphique
      if (match.start > lastIndex) {
        const textBefore = text.slice(lastIndex, match.start);
        if (textBefore.trim()) {
          parts.push(
            <div key={`text-${chartIndex}`} className="mb-6">
              <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900 prose-li:text-gray-800 prose-a:text-blue-600">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                >
                  {textBefore}
                </ReactMarkdown>
              </div>
            </div>
          );
        }
      }

      // Parser et rendre le graphique
      try {
        const cleanedJson = match.jsonString.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
        const chartSpec: ChartSpec = JSON.parse(cleanedJson);

        if (chartSpec && chartSpec.type && chartSpec.data && Array.isArray(chartSpec.data)) {
          // Créer une clé stable basée sur le contenu JSON pour éviter les re-renders
          const stableKey = `chart-${createStableKey(match.jsonString)}`;
          parts.push(renderChart(chartSpec, stableKey));
          logger.log('Chart rendu avec succès:', chartSpec.title);
        } else {
          throw new Error('Structure de chart invalide');
        }
        chartIndex++;
      } catch (error) {
        logger.error('Erreur parsing chart:', error, 'JSON:', match.jsonString.substring(0, 200));
        parts.push(
          <div key={`error-${createStableKey(match.jsonString)}`} className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm mb-2">Graphique non parsé - Vérification JSON:</p>
            <pre className="text-xs text-theme-primary overflow-auto bg-theme-surface/50 p-2 rounded max-h-40">{match.jsonString.substring(0, 500)}</pre>
            <p className="text-red-500 text-xs mt-2">Erreur: {(error as Error).message}</p>
          </div>
        );
        chartIndex++;
      }

      lastIndex = match.end;
    }

    // Ajouter le texte restant après le dernier graphique
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText.trim()) {
        parts.push(
          <div key={`text-final`} className="mb-6">
            <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900 prose-li:text-gray-800 prose-a:text-blue-600">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
              >
                {remainingText}
              </ReactMarkdown>
            </div>
          </div>
        );
      }
    }

    // Si aucun graphique trouvé, rendre tout le texte en markdown
    return parts.length > 0 ? parts : [
      <div key="full-content" className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900 prose-li:text-gray-800 prose-a:text-blue-600">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
        >
          {text}
        </ReactMarkdown>
      </div>
    ];
  };

  const renderChart = (chartSpec: ChartSpec, key: string) => {
    const { type, title, data } = chartSpec;

    // Validation des données
    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <div key={`${key}-error`} className="mb-8 p-6 bg-theme-surface border border-theme rounded-lg">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">{title}</h3>
          <p className="text-theme-text-secondary">Aucune donnée disponible pour ce graphique</p>
        </div>
      );
    }

    // Dashboard KPI (type spécial)
    if (type === 'dashboard') {
      return (
        <div key={`${key}-dashboard`} className="mb-8 p-6 bg-gradient-to-br from-gray-800 to-gray-900 border border-orange-600/30 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold text-theme-primary mb-6 flex items-center gap-2">
            <span className="text-2xl"></span> {title}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.map((kpi, idx) => {
              const metric = kpi.metric || kpi.label || 'Métrique';
              const current = kpi.current !== undefined ? kpi.current : (kpi.value !== undefined ? kpi.value : 0);
              const target = kpi.target || 0;
              const unit = kpi.unit || '';
              const progress = target > 0 ? (current / target) * 100 : 0;
              const isGood = progress >= 75;
              
              return (
                <div key={idx} className="bg-theme-surface/50 p-4 rounded-lg border border-gray-700/50 hover:border-orange-600/30 transition-all">
                  <div className="text-xs text-theme-primary mb-1">{metric}</div>
                  <div className={`text-2xl font-bold ${isGood ? 'text-green-400' : 'text-purple-600 dark:text-purple-400'}`}>
                    {current}{unit}
                  </div>
                  {target > 0 && (
                    <>
                      <div className="text-xs text-theme-secondary mt-1">
                        Objectif: {target}{unit}
                      </div>
                      <div className="mt-2 w-full bg-theme-hover rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full ${isGood ? 'bg-theme-text-primary' : 'bg-theme-text-secondary'}`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Transformation sécurisée des données selon le type de graphique
    let chartData: any[] = [];
    
    if (type === 'scatter') {
      // Pour scatter, on a besoin de x et y
      chartData = data
        .filter(item => item && 
          (typeof item.x === 'number' || typeof item.risk_probability === 'number' || typeof item.probability === 'number') &&
          (typeof item.y === 'number' || typeof item.financial_impact === 'number' || typeof item.impact === 'number') &&
          !isNaN(Number(item.x || item.risk_probability || item.probability)) &&
          !isNaN(Number(item.y || item.financial_impact || item.impact))
        )
        .map(item => ({
          name: item.label || item.phase || item.metric || 'Point',
          x: Number(item.x || item.risk_probability || item.probability || 0),
          y: Number(item.y || item.financial_impact || item.impact || 0)
        }));
    } else {
      // Pour bar, pie, line - Parser plus flexible qui trouve automatiquement la valeur numérique
      chartData = data
        .map(item => {
          if (!item || typeof item !== 'object') return null;
          
          // Trouver le label (priorité aux propriétés communes)
          const labelKey = Object.keys(item).find(k => 
            ['label', 'name', 'metric', 'mois', 'month', 'periode', 'segment', 'categorie', 'type'].includes(k.toLowerCase())
          );
          const name = labelKey ? String(item[labelKey]) : 'Sans nom';
          
          // Trouver la valeur numérique (priorité aux propriétés communes, puis chercher n'importe quel nombre)
          let value = 0;
          
          // 1. Chercher dans les propriétés standards
          const standardKeys = ['value', 'current', 'ca', 'roi', 'montant', 'amount', 'total', 'valeur', 'revenu', 'revenue'];
          const standardKey = Object.keys(item).find(k => standardKeys.includes(k.toLowerCase()));
          
          if (standardKey) {
            const rawValue = item[standardKey];
            // Gérer les strings qui représentent des nombres
            if (typeof rawValue === 'number') {
              value = rawValue;
            } else if (typeof rawValue === 'string' && !isNaN(parseFloat(rawValue))) {
              value = parseFloat(rawValue);
            }
          } else {
            // 2. Si pas trouvé, chercher n'importe quelle propriété numérique (qui n'est pas un ID)
            const numericKey = Object.keys(item).find(k => {
              const v = item[k];
              const isValidKey = !['id', 'index', 'order'].includes(k.toLowerCase());
              
              if (typeof v === 'number') {
                return isValidKey && !isNaN(v);
              } else if (typeof v === 'string') {
                return isValidKey && !isNaN(parseFloat(v));
              }
              return false;
            });
            
            if (numericKey) {
              const rawValue = item[numericKey];
              value = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
            }
          }
          
          // Valider que la valeur est un nombre valide (0 est accepté)
          if (typeof value !== 'number' || isNaN(value)) {
            return null;
          }
          
          return { name, value };
        })
        .filter(item => item !== null) as Array<{name: string, value: number}>;
    }

    if (chartData.length === 0) {
      return (
        <div key={`${key}-nodata`} className="mb-8 p-6 bg-theme-surface border border-theme rounded-lg">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">{title}</h3>
          <p className="text-theme-text-secondary">Données invalides pour ce graphique</p>
        </div>
      );
    }

    return (
      <div key={key} className="mb-8 p-6 bg-theme-surface border border-theme rounded-lg shadow-theme-sm">
        <h3 className="text-lg font-semibold text-theme-text-primary mb-4">{title}</h3>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            {type === 'bar' ? (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="name" stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <YAxis stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="value" fill="#0088FE" />
              </BarChart>
            ) : type === 'pie' ? (
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            ) : type === 'line' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="name" stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <YAxis stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Line type="monotone" dataKey="value" stroke="#0088FE" strokeWidth={2} />
              </LineChart>
            ) : type === 'area' ? (
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="name" stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <YAxis stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Area type="monotone" dataKey="value" fill="#0088FE" stroke="#0088FE" fillOpacity={0.6} />
              </AreaChart>
            ) : type === 'combo' || type === 'composed' ? (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="name" stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <YAxis stroke={isDark ? "#9ca3af" : "#6b7280"} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="value" fill="#0088FE" />
                <Line type="monotone" dataKey="value2" stroke="#00C49F" strokeWidth={2} />
              </ComposedChart>
            ) : type === 'scatter' ? (
              <ScatterChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name="Probabilité" 
                  stroke={isDark ? "#9ca3af" : "#6b7280"}
                  label={{ value: 'Probabilité (%)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name="Impact" 
                  stroke={isDark ? "#9ca3af" : "#6b7280"}
                  label={{ value: 'Impact (€)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={tooltipStyle}
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(value: any, name: string) => {
                    if (name === 'x') return [`${value}%`, 'Probabilité'];
                    if (name === 'y') return [`${value}€`, 'Impact'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Scatter name="Risques" data={chartData} fill="#FF8042" />
              </ScatterChart>
            ) : (
              <div className="flex items-center justify-center h-full text-theme-text-secondary">
                Type de graphique "{type}" non supporté. Types valides: bar, pie, line, area, combo, scatter, dashboard
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Mémoriser le rendu des graphiques pour éviter les re-renders pendant le streaming
  // On utilise une clé basée sur la longueur du contenu pour permettre les mises à jour progressives
  const memoizedContent = useMemo(() => {
    return renderCharts(content);
  }, [content, isDark]); // Re-render uniquement si le contenu ou le thème change

  return (
    <div 
      className={`deep-report-content ${className}`}
      style={{
        color: '#1F2937'
      }}
    >
      <style dangerouslySetInnerHTML={{
        __html: `
          .deep-report-content h1 {
            color: #111827 !important;
            font-weight: bold;
            font-size: 1.875rem;
            margin-bottom: 1rem;
          }
          .deep-report-content h2 {
            color: #1E40AF !important;
            font-weight: 600;
            font-size: 1.5rem;
            margin-bottom: 0.75rem;
            margin-top: 1.5rem;
          }
          .deep-report-content h3 {
            color: #6D28D9 !important;
            font-weight: 500;
            font-size: 1.25rem;
            margin-bottom: 0.5rem;
            margin-top: 1rem;
          }
          .deep-report-content p {
            color: #374151 !important;
            line-height: 1.7;
            margin-bottom: 1rem;
          }
          .deep-report-content strong {
            color: #111827 !important;
            font-weight: 600;
          }
          .deep-report-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 1rem 0;
            background: #FFFFFF;
            border-radius: 0.5rem;
            overflow: hidden;
            border: 1px solid #E5E7EB;
          }
          .deep-report-content th {
            background: #F3F4F6 !important;
            color: #111827 !important;
            padding: 0.75rem;
            text-align: left;
            font-weight: 600;
            border-bottom: 2px solid #D1D5DB;
          }
          .deep-report-content td {
            color: #4B5563 !important;
            padding: 0.75rem;
            border-bottom: 1px solid #E5E7EB;
          }
          .deep-report-content tr:hover {
            background: #F9FAFB;
          }
          .deep-report-content ul, .deep-report-content ol {
            color: #374151 !important;
            padding-left: 1.5rem;
            margin: 1rem 0;
          }
          .deep-report-content li {
            color: #374151 !important;
            margin-bottom: 0.5rem;
          }
          .deep-report-content code {
            background: #F3F4F6 !important;
            color: #D97706 !important;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
            border: 1px solid #E5E7EB;
          }
          .deep-report-content pre {
            background: #F9FAFB !important;
            color: #059669 !important;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin: 1rem 0;
            border: 1px solid #E5E7EB;
          }
        `
      }} />
      {memoizedContent}
    </div>
  );
};

export default DeepReportRenderer;
